module OnlineDPR.Application.Client.InvoiceComponents

open System

open Fable.React
open Fable.React.Props
open Browser.Types
open OnlineDPR.Application.Shared
open OnlineDPR.Application.Shared.Translations

let userNotesDisplayOrInput d enabled onChange =
    let raiseUpdateIfChanged (e:FocusEvent) =
        let textFromInput = ((unbox<HTMLInputElement> e.target).value)
        match String.Compare(d.UserNotes |> Option.defaultValue "", textFromInput) with
        | 0 -> ()
        | _ -> onChange {d with UserNotes = Some textFromInput}
    let keyPress (e:KeyboardEvent) = match e.key with
                                     | "Enter" -> ((unbox<HTMLInputElement> e.target).blur())
                                     | _ -> ()
    match enabled with
    | true -> input [ Type "text"
                      DefaultValue d.UserNotes
                      OnBlur (fun e -> raiseUpdateIfChanged e)
                      OnKeyPress (fun e -> keyPress e)
                      MaxLength 20.0 ]
    | false -> str (d.UserNotes |> Option.defaultValue "")

let rec private invoiceRow (docs:Invoice list) editUserNotesEnabled (onChange : (Invoice -> unit)) =
    docs
    |> List.map (fun d -> ( li [ Class "item"
                                 Key (string d.InvoiceId) ]
                               [ div [ Class "item-row"]
                                     [
                                       div [ Class "item-col pull-left item-col-title"]
                                           [ div [ Class "item-heading"] [ str (CurrentTranslations.Translations.[TranslationKey.ControlPanel_Invoices_Column_Invoice_Number_Title]) ]
                                             div []
                                                 [ a [ Href d.URl
                                                       TabIndex -1 ]
                                                     [ h4 [ Class "item-title" ] [ str d.InvoiceNumber] ] ]]
                                       div [ Class "item-col item-col-date" ]
                                           [ div [ Class "item-heading"] [ str (CurrentTranslations.Translations.[TranslationKey.ControlPanel_Invoices_Column_Invoice_Date_Title]) ]
                                             div [ Class "no-overflow" ] [ str d.InvoiceDate ] ]
                                       div [ Class "item-col item-col-date" ]
                                           [ div [ Class "item-heading"] [ str (CurrentTranslations.Translations.[TranslationKey.ControlPanel_Invoices_Column_DueDate_Title])]
                                             div [ Class "no-overflow" ] [ str d.DueDate ] ]
                                       div [ Class "item-col item-col-number" ]
                                           [ div [ Class "item-heading"] [ str (CurrentTranslations.Translations.[TranslationKey.ControlPanel_Invoices_Column_Club_Number_Title])]
                                             div [ Class "no-overflow" ] [ str d.ClubNumber ] ]
                                       div [ Class "item-col" ]
                                           [ div [ Class "item-heading"] [ str (CurrentTranslations.Translations.[TranslationKey.ControlPanel_Invoices_Column_Product_Title])]
                                             div [ Class "no-overflow" ] [ str d.Product ] ]
                                       div [ Class "item-col item-col-number" ]
                                           [ div [ Class "item-heading"] [ str (CurrentTranslations.Translations.[TranslationKey.ControlPanel_Invoices_Column_Invoice_Amount_Title])]
                                             div [ Class "no-overflow" ] [ str d.Amount ] ]
                                       div [ Class "item-col" ]
                                           [ div [ Class "item-heading"] [ str (CurrentTranslations.Translations.[TranslationKey.ControlPanel_Invoices_Column_Notes_Title]) ]
                                             div [ Class "no-overflow" ] [ userNotesDisplayOrInput d editUserNotesEnabled onChange ] ]
                                       div [ Class "item-col fixed item-col-actions-dropdown"]
                                           [ div [ Class "item-actions-dropdown" ]
                                                 [ a [ Class ""
                                                       Download ""
                                                       Href d.URl
                                                       TabIndex -1 ]
                                                     [ i [ Class "fa fa-download"
                                                           Title (CurrentTranslations.Translations.[TranslationKey.ControlPanel_Invoices_Column_Actions_Download])]
                                                         [ ] ] ] ] ] ] ))

let private invoiceRows (docs:Invoice list) editUserNotesEnabled onChange =
    match docs.Length with
    | 0 -> [ li [ Class "item"
                  Key "no-invoice" ]
                [ div [ Class "item-row"]
                      [ div [ Class "item-col fixed item-col-check"] [ span [] [] ]
                        div [ Class "item-col"] [ str (CurrentTranslations.Translations.[TranslationKey.ControlPanel_Invoices_No_Invoices_Message]) ] ] ] ]
    | _ -> invoiceRow docs editUserNotesEnabled onChange

let private invoiceHeaderBase (content: ReactElement) (className: string) =
    div [ Class ("item-col item-col-header " + className) ]
        [ div [] [ content ] ]

let private invoiceHeader (label: string) (className: string) =
    let content = span [] [str label]
    invoiceHeaderBase content className

let private onSortChange (column: InvoiceSortColumn) (currentOption: InvoiceSortOption) =
    match currentOption.Column, currentOption.Order with
    | c, InvoiceSortOrder.Ascending when c = column -> { Column = column
                                                         Order = InvoiceSortOrder.Descending }
    | c, InvoiceSortOrder.Descending when c = column -> { Column = column
                                                          Order = InvoiceSortOrder.Ascending }
    | _ -> { Column = column
             Order = InvoiceSortOrder.Descending }

let private invoiceSortIcon (column: InvoiceSortColumn) (currentOption: InvoiceSortOption) =
    match currentOption.Column, currentOption.Order with
    | c, InvoiceSortOrder.Ascending when c = column -> i [ ClassName "fa fa-sort-up icon" ] [ ]
    | c, InvoiceSortOrder.Descending when c = column -> i [ ClassName "fa fa-sort-down icon" ] [ ]
    | _ -> i [ ClassName "fa fa-sort icon" ] [ ]

let private sortableInvoiceHeader (column: InvoiceSortColumn) (currentOption: InvoiceSortOption) (onChange: InvoiceSortOption -> unit) =
    let label, className = match column with
                           | Number -> CurrentTranslations.Translations.[TranslationKey.ControlPanel_Invoices_Column_Invoice_Number_Title], "item-col-title sortable"
                           | Date -> CurrentTranslations.Translations.[TranslationKey.ControlPanel_Invoices_Column_Invoice_Date_Title], "item-col-date sortable"
                           | DueDate -> CurrentTranslations.Translations.[TranslationKey.ControlPanel_Invoices_Column_DueDate_Title], "item-col-date sortable"
                           | ClubNumber -> CurrentTranslations.Translations.[TranslationKey.ControlPanel_Invoices_Column_Club_Number_Title], "item-col-number sortable"
                           | Product -> CurrentTranslations.Translations.[TranslationKey.ControlPanel_Invoices_Column_Product_Title], "sortable"
                           | Amount -> CurrentTranslations.Translations.[TranslationKey.ControlPanel_Invoices_Column_Invoice_Amount_Title], "item-col-number sortable"
                           | Note -> CurrentTranslations.Translations.[TranslationKey.ControlPanel_Invoices_Column_Notes_Title], "sortable"
                           | _ -> "", "sortable"
    let sortIcon = invoiceSortIcon column currentOption
    let content = a [ OnClick (fun _ -> (onChange (onSortChange column currentOption)))
                      Href "#"]
                    [ sortIcon
                      span [] [ str label ] ]
    invoiceHeaderBase content className

let sortInvoices (invoices: Invoice list) (sorting: InvoiceSortOption) =
    let sorted = match sorting.Column with
                 | Number -> List.sortBy (fun invoice -> invoice.InvoiceNumber) invoices
                 | Date -> List.sortBy (fun invoice -> invoice.InvoiceDateTimeStamp) invoices
                 | DueDate -> List.sortBy (fun invoice -> invoice.DueDateTimeStamp) invoices
                 | ClubNumber -> List.sortBy (fun invoice -> invoice.ClubNumber) invoices
                 | Product -> List.sortBy (fun invoice -> invoice.Product) invoices
                 | Amount -> List.sortBy (fun invoice -> invoice.AmountValue) invoices
                 | Note -> List.sortBy (fun invoice -> invoice.UserNotes) invoices
                 | _ -> invoices
    match sorting.Order with
    | Descending -> List.rev sorted
    | Ascending -> sorted

type InvoiceListComponentProps = {
    invoices : Invoice list
    editUserNotesEnabled: Boolean
    onChange: Invoice -> unit
    sortOption: InvoiceSortOption
    onSortOptionChanged: InvoiceSortOption -> unit }

let invoiceList ({ invoices = invoices; editUserNotesEnabled = editUserNotesEnabled; onChange = onChange; sortOption = sortOption; onSortOptionChanged = onSortOptionChanged }:InvoiceListComponentProps) =
    ul [ Class "item-list striped"]
       [ yield li [ Class "item item-list-header"
                    Key "invoice-list-header" ]
                  [ div [ Class "item-row" ]
                        [ sortableInvoiceHeader Number sortOption onSortOptionChanged
                          sortableInvoiceHeader Date sortOption onSortOptionChanged
                          sortableInvoiceHeader DueDate sortOption onSortOptionChanged
                          sortableInvoiceHeader ClubNumber sortOption onSortOptionChanged
                          sortableInvoiceHeader Product sortOption onSortOptionChanged
                          sortableInvoiceHeader Amount sortOption onSortOptionChanged
                          sortableInvoiceHeader Note sortOption onSortOptionChanged
                          invoiceHeader "" "fixed item-col-actions-dropdown" ] ];
          yield ofList (invoiceRows (sortInvoices invoices sortOption) editUserNotesEnabled onChange) ]

let inline InvoiceListComponent invoices editUserNotesEnabled onChange sortOption onSortOptionChanged =
  (ofFunction invoiceList) ({ invoices = invoices; editUserNotesEnabled = editUserNotesEnabled; onChange = onChange; sortOption = sortOption; onSortOptionChanged = onSortOptionChanged }:InvoiceListComponentProps) []